// Cuarto artículo: qué llevar en la maleta para esquiar con niños.
// Query objetivo: "qué llevar maleta esquí niños" · "equipaje esquí familia" · "ropa esquí niños"

export const post = {
  meta: {
    slug: "que-llevar-maleta-esqui-ninos",
    title: "Qué llevar en la maleta para esquiar con niños (lista completa)",
    description:
      "Lista definitiva de qué meter en la maleta para esquiar con niños: ropa técnica por capas, cuántos pares de calcetines, qué alquilar y qué comprar, botiquín y errores que arruinan el viaje.",
    date: "2026-10-01",
    reading_minutes: 10,
    cover: "/blog/que-llevar-maleta-esqui-ninos.jpg",
    cover_alt:
      "Maleta abierta con ropa de esquí para niños: chaqueta, térmicas, calcetines de lana, guantes y gafas",
    author: "Club Amistad Madrid",
    category: "Guías prácticas",
    keywords: [
      "qué llevar maleta esquí niños",
      "equipaje viaje esquí familia",
      "ropa esquí niños qué comprar",
      "lista maleta nieve niños",
      "capas ropa esquí infantil",
    ],
    related_trips: [
      "cerler-navidad",
      "masella-inmaculada",
      "cerler-escapada-enero",
      "sierra-nevada-escapada-enero",
    ],
    faqs: [
      {
        q: "¿Cuántos conjuntos de ropa técnica hay que llevar por niño?",
        a: "Dos juegos completos de ropa interior térmica (camiseta + mallas) y un pantalón y chaqueta de nieve. El pantalón y la chaqueta se reutilizan toda la semana; lo que se cambia a diario son las térmicas y los calcetines. Con dos juegos vas sobrado: uno puesto y otro secándose en el radiador del hotel.",
      },
      {
        q: "¿Cuántos pares de calcetines de esquí necesita un niño?",
        a: "Uno por día de esquí, más uno de repuesto. Calcetín alto, fino, de lana merina o técnico — NUNCA dos pares a la vez ni calcetines de algodón: hacen arrugas, el pie suda y acaba frío y con rozaduras.",
      },
      {
        q: "¿Qué es mejor, comprar o alquilar el equipo de esquí de los niños?",
        a: "Alquilar esquís, botas y casco. Crecen cada temporada y el alquiler cuesta 58-80 € la semana con casco incluido. Merece la pena comprar solo lo que va pegado al cuerpo: térmicas, calcetines, guantes y gafas de ventisca.",
      },
      {
        q: "¿Hacen falta gafas de ventisca o valen unas de sol?",
        a: "Gafas de ventisca (máscara). Las de sol no protegen del viento ni de la nieve, se caen al primer tropiezo y no encajan con el casco. Para niños, máscara con categoría 2-3 y banda ancha sobre el casco.",
      },
      {
        q: "¿Qué no hay que meter en la maleta?",
        a: "Vaqueros (se empapan y no secan), ropa de algodón como capa interior, calzado sin suela antideslizante, bufandas largas sueltas (peligrosas en remontes) y el abrigo urbano de la ciudad. Ocupan media maleta y no se usan.",
      },
    ],
  },
  content: `# Qué llevar en la maleta para esquiar con niños

La noche antes del viaje, con la maleta abierta en el suelo del salón, siempre aparece la misma duda: *"¿Me estoy olvidando de algo importante?"*.

Después de 56 años organizando viajes de esquí en familia, hemos visto las dos caras: familias que llegan con tres maletas enormes y la mitad sin usar, y familias que llegan con una mochila y se pasan el primer día comprando guantes en la tienda de la estación a precio de estación.

Esta es la lista real, probada en el Pirineo y en Sierra Nevada, de **lo que sí hace falta** y de lo que puedes dejar en casa.

## La regla de oro: las tres capas

Todo el equipaje de nieve de un niño se entiende con una idea muy simple. No se abriga con una prenda gorda, se abriga con **tres capas finas** que trabajan juntas:

| Capa | Qué es | Su trabajo |
|---|---|---|
| **1ª · Interior** | Camiseta y malla térmica (lana merina o sintética) | Saca el sudor de la piel. Es la capa más importante |
| **2ª · Intermedia** | Forro polar o sudadera técnica | Guarda el calor |
| **3ª · Exterior** | Chaqueta y pantalón de nieve impermeables | Para el viento, la nieve y el agua |

¿Por qué importa tanto? Porque **un niño mojado es un niño que quiere volver al hotel**. El 90% de las lágrimas en pista no son por frío ambiental: son por humedad. Sudan subiendo, se paran en la cola del remonte y ahí llega el frío.

Y por eso el algodón está prohibido como primera capa: absorbe el sudor y lo retiene pegado a la piel. Una camiseta de algodón debajo del mono es el billete directo a "papá, me quiero ir".

## Lista de ropa por niño (viaje de 5 días)

**Para esquiar:**

- 2 camisetas térmicas de manga larga
- 2 mallas térmicas
- 1 forro polar (y un segundo fino, si hay sitio)
- 1 pantalón de nieve impermeable (o mono entero si tiene menos de 6 años)
- 1 chaqueta de nieve impermeable con capucha
- 5-6 pares de calcetines de esquí altos y finos
- 2 pares de guantes o manoplas impermeables — **dos, siempre dos**
- 1 braga de cuello (el tubo de forro polar, no bufanda)
- 1 gorro fino que quepa bajo el casco
- 1 máscara de ventisca
- Crema solar factor 50 y stick labial con protección

**Para el resto del día (hotel, cena, actividades):**

- 1 pantalón cómodo por día (chándal o similar)
- 2-3 sudaderas o jerséis
- Ropa interior y pijama
- 1 par de botas de nieve o apreskí con suela antideslizante
- 1 par de zapatillas para estar dentro del hotel
- Bañador y chanclas (muchos hoteles tienen piscina o spa)
- Mochila pequeña para el agua y el snack

**Lo que no se te puede olvidar aunque parezca menor:**

- Mochila o bolsa para la ropa mojada
- Bolsa de plástico para las botas si las lleváis vosotros
- Pañuelos de papel (en el bolsillo de la chaqueta, no en la maleta)
- Cargadores y batería externa
- Tarjeta sanitaria de cada niño

## Las 2 manoplas: el consejo que más nos agradecen

Si solo te quedas con un consejo de este artículo, que sea este: **lleva dos pares de guantes por niño**.

Los guantes se mojan. Siempre. Jugando con nieve en la cola, al caerse, al quitarse el casco con las manos llenas de nieve. Un guante mojado tarda toda la noche en secarse y a media mañana del día siguiente las manos están congeladas.

Con dos pares, se cambian al mediodía y el día está salvado. Para menores de 7 años, **manoplas** mejor que guantes de cinco dedos: los dedos se dan calor entre sí y además se las ponen solos sin pelearse.

## Qué alquilar y qué comprar

Esta es la decisión que más dinero ahorra o derrocha.

**Alquila sin dudarlo:** esquís, botas, bastones y casco. Un niño cambia de talla de bota cada temporada, y el alquiler sale entre **58 € y 80 € la semana con casco incluido**. Comprar un equipo infantil completo cuesta cuatro veces más y le vale un año.

**Compra solo lo que toca la piel:** térmicas, calcetines, guantes y máscara. Son barato comparado con el resto y la diferencia de confort es enorme.

**El caso intermedio:** la chaqueta y el pantalón de nieve. Si es vuestro primer viaje y no sabéis si repetiréis, se pueden alquilar o heredar de un primo. Si ya sabéis que la nieve os va a engachar, compradlos una talla por encima: duran dos temporadas.

En nuestros viajes el alquiler se gestiona **en destino, el día de llegada**, con tallaje hecho por el personal de la tienda y con cambio gratuito si la bota aprieta. No tienes que medir nada en casa ni cargar con material en el coche.

## El botiquín familiar de nieve

Pequeño, en una bolsa de tela, en la maleta de mano:

- Paracetamol / ibuprofeno infantil en la dosis de cada niño
- Tiritas y gasas (las rozaduras de bota son el clásico)
- Crema para golpes (árnica)
- Antihistamínico si alguno es alérgico
- Medicación habitual con su receta
- Biodramina o similar: las carreteras de montaña tienen muchas curvas
- Termómetro

La crema solar merece su propio párrafo: **en la nieve el sol pega desde arriba y desde abajo**. La nieve refleja hasta el 80% de la radiación. Factor 50, cara y orejas, reaplicar a mediodía, y stick de labios en el bolsillo de la chaqueta. Las quemaduras de montaña sorprenden incluso en días nublados.

## Cómo hacer la maleta para que todo funcione

Un par de trucos muy sencillos que cambian la logística de la semana:

1. **Una bolsa por día y por niño.** Térmicas + calcetines + ropa interior de cada día en una bolsa de tela o una bolsa zip. Por la mañana coges la bolsa del día y listo: nadie revuelve la maleta a las 8:00.
2. **La ropa de esquiar, encima.** Es lo primero que se necesita al llegar, porque muchas familias esquían ya la tarde del primer día.
3. **La mochila de mano, con lo irrenunciable:** guantes, braga, crema solar y una muda. Si la maleta se queda en el maletero o llega tarde, el día está salvado.
4. **Bolsa específica para ropa mojada.** Si no, el domingo abres la maleta en casa y todo huele a vestuario.
5. **Nombre en todo lo que se quita.** Guantes, gorros y bragas desaparecen en la bolsa común de un grupo de 30 niños. Una etiqueta con el nombre evita el drama.

## Los 5 errores que vemos cada temporada

- **Vaqueros para la nieve.** Se empapan en diez minutos y no secan en toda la noche.
- **Dos pares de calcetines "para más calor".** Hacen arrugas dentro de la bota, aprietan, cortan la circulación y el pie acaba más frío. Uno, fino y alto.
- **Bufanda larga.** Preciosa en la foto, peligrosa en un remonte y en una silla. Braga de cuello.
- **Gafas de sol en vez de máscara.** Al primer tropiezo acaban en la nieve y no protegen del viento.
- **Media maleta de ropa "de ciudad".** En un hotel a pie de pista se vive en chándal y sudadera. Nadie se arregla para cenar.

## Y si se nos olvida algo, ¿pasa algo?

No. En nuestros destinos siempre hay tienda en la estación o en el pueblo, y el coordinador de Club Amistad está allí para resolverlo. Es más caro que comprarlo en casa, pero nada se arruina por un gorro olvidado.

Lo que de verdad marca la diferencia es llegar con la ropa correcta, calcetines suficientes y **dos pares de guantes**. Con eso, el resto del viaje ya está pensado por nosotros.

## ¿Preparando vuestro primer viaje a la nieve?

Si quieres, te contamos exactamente qué necesitaréis para vuestras fechas, la edad de los peques y la estación elegida. Sin compromiso.

- 📞 **914 020 331** (L-V 10:00-19:00 · verano 10:00-14:00)
- 📧 informacion@clubamistad.com
- 📅 [Pide información del viaje que te encaje](/#pide-info)

**Enlaces relacionados:**
- [Guía: primer viaje de esquí con niños](/blog/primer-viaje-esqui-con-ninos)
- [Cuánto cuesta un viaje de esquí en familia](/blog/cuanto-cuesta-viaje-esqui-familia-2027)
- [Cerler vs Masella: cuál elegir](/blog/cerler-vs-masella-familias)
- [Ver todos los viajes 2026-27](/#viajes)

*Última actualización: octubre 2026*
`,
};
